import { Link } from "react-router-dom";
import { Clock, Car, Calendar, MapPin, Wrench, Sparkles, ArrowUpRight, BookOpen, Trophy, CalendarCheck, MessageCircle } from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import ApplicationCard from "@/components/ApplicationCard";
import { useTrackView } from "@/lib/useTrackView";
import { useSettings } from "@/context/SettingsContext";
import { whatsappLink, telLink, serviceWhatsAppMessage } from "@/lib/contact";
import { articleSchema, breadcrumbSchema } from "@/lib/schema";
import FinalCta from "@/sections/FinalCta";

function Block({ number, title, children }) {
  return (
    <div>
      <div className="flex items-center gap-3">
        {number && <span className="font-oswald text-sm font-bold tracking-[0.2em] text-gold">{number}</span>}
        <h2 className="font-display text-2xl font-medium text-white sm:text-[1.7rem]">{title}</h2>
      </div>
      <div className="mt-4 text-base leading-relaxed text-neutral-300">{children}</div>
    </div>
  );
}

export default function ApplicationDetail({ app: c }) {
  useTrackView("application", c.slug);
  const settings = useSettings();

  const primaryService = c.related_service_objs?.[0]?.name || (c.services || [])[0] || "araç koruma";
  const waMessage = serviceWhatsAppMessage(primaryService, c.district);
  const crumbs = [
    { label: "Ana Sayfa", to: "/" },
    { label: "Uygulamalar", to: "/uygulamalar" },
    ...(c.district ? [{ label: c.district, to: `/uygulamalar/${c.district_slug}` }] : []),
    { label: c.title },
  ];
  const schema = [
    articleSchema({ title: c.title, description: c.summary, image: c.cover_image || c.after_image, url: `/uygulamalar/${c.slug}`, datePublished: c.created_at }),
    breadcrumbSchema(crumbs),
  ];
  const paragraphs = (c.content || "").split("\n").filter((p) => p.trim());
  const facts = [
    { icon: Car, label: "Araç", value: `${c.brand || ""} ${c.model || ""}`.trim() },
    { icon: Calendar, label: "Model Yılı", value: c.year },
    { icon: MapPin, label: "İlçe", value: c.district },
    { icon: Clock, label: "Süre", value: c.duration },
    { icon: Wrench, label: "Yöntem", value: c.method },
  ];

  return (
    <>
      <Seo title={`${c.title} | Meguiar's İzmit`} description={c.summary} image={c.cover_image || c.after_image} canonicalPath={`/uygulamalar/${c.slug}`} jsonLd={schema} />
      <PageHero
        overline={[c.district, c.year ? `${c.year} Model` : null].filter(Boolean).join(" · ")}
        title={
          <>
            <span className="block" data-testid="application-vehicle">{`${c.brand || ""} ${c.model || ""}`.trim() || c.title}</span>
            {(c.services || []).length > 0 && (
              <span className="mt-4 block font-oswald text-lg font-semibold uppercase tracking-[0.15em] text-gold sm:text-xl" data-testid="application-service-line">
                {c.services.join(" + ")}
              </span>
            )}
          </>
        }
        imageAlt={c.title}
        subtitle={c.summary}
        image={c.cover_image || c.after_image}
        breadcrumbs={crumbs}
      >
        <div className="mt-8 flex flex-wrap gap-3">
          <Link to={`/randevu?hizmet=${encodeURIComponent(primaryService)}`} className="inline-flex items-center gap-2 bg-gold px-6 py-3.5 font-oswald text-sm font-bold uppercase tracking-[0.1em] text-black transition-colors hover:bg-gold-300" data-testid="application-randevu-btn">
            <CalendarCheck className="h-[18px] w-[18px]" /> Aracım İçin Randevu
          </Link>
          <a href={whatsappLink(settings, waMessage)} target="_blank" rel="noopener noreferrer" data-testid="application-whatsapp-btn" className="inline-flex items-center gap-2 border border-white/15 bg-white/[0.04] px-6 py-3.5 font-oswald text-sm font-semibold uppercase tracking-[0.1em] text-white transition-colors hover:border-gold/50">
            <MessageCircle className="h-[18px] w-[18px] text-gold" /> WhatsApp'tan Yaz
          </a>
        </div>
      </PageHero>

      <section className="obsidian py-16 sm:py-24">
        <div className="container-x grid gap-12 lg:grid-cols-[1.6fr_0.9fr]">
          <div className="space-y-14">
            {(c.before_image || c.after_image) && (
              <Reveal>
                <Block number="01" title="Öncesi / Sonrası">
                  <div className="mt-2 grid grid-cols-2 gap-3 sm:gap-4">
                    <figure className="relative overflow-hidden border border-white/15 bg-neutral-900" data-testid="application-before">
                      <div className="aspect-[4/5]">
                        {c.before_image
                          ? <img src={`${c.before_image}?w=640`} alt={`${c.title} - öncesi`} loading="lazy" decoding="async" className="h-full w-full object-cover" />
                          : <div className="flex h-full w-full items-center justify-center text-xs text-neutral-600">Görsel yok</div>}
                      </div>
                      <figcaption className="absolute left-3 top-3 border border-white/25 bg-black/70 px-3 py-1 font-oswald text-[11px] font-bold tracking-[0.2em] text-white backdrop-blur">ÖNCE</figcaption>
                    </figure>
                    <figure className="relative overflow-hidden border border-gold/40 bg-neutral-900" data-testid="application-after">
                      <div className="aspect-[4/5]">
                        {c.after_image
                          ? <img src={`${c.after_image}?w=640`} alt={`${c.title} - sonrası`} loading="lazy" decoding="async" className="h-full w-full object-cover" />
                          : <div className="flex h-full w-full items-center justify-center text-xs text-neutral-600">Görsel yok</div>}
                      </div>
                      <figcaption className="absolute left-3 top-3 border border-gold/50 bg-black/70 px-3 py-1 font-oswald text-[11px] font-bold tracking-[0.2em] text-gold backdrop-blur">SONRA</figcaption>
                    </figure>
                  </div>
                </Block>
              </Reveal>
            )}

            {paragraphs.length > 0 && (
              <Reveal>
                <Block number="02" title="Uygulama hikâyesi">
                  {paragraphs.map((p, i) => <p key={i} className={i === 0 ? "" : "mt-4"}>{p}</p>)}
                </Block>
              </Reveal>
            )}

            {c.result && (
              <Reveal>
                <div className="border border-gold/25 bg-gold/[0.04] p-6 sm:p-7" data-testid="application-result">
                  <div className="flex items-center gap-3">
                    <Trophy className="h-5 w-5 text-gold" />
                    <h3 className="font-display text-xl font-medium text-white">Sonuç</h3>
                  </div>
                  <p className="mt-3 text-base leading-relaxed text-neutral-200">{c.result}</p>
                </div>
              </Reveal>
            )}

            {c.gallery?.length > 0 && (
              <Reveal>
                <Block number="03" title="Galeri">
                  <div className="mt-2 grid grid-cols-2 gap-2.5 sm:grid-cols-3">
                    {c.gallery.map((g, i) => (
                      <div key={i} className="aspect-[9/16] overflow-hidden border border-white/10">
                        <img src={`${g}?w=480`} alt={`${c.title} ${i + 1}`} loading="lazy" decoding="async" className="h-full w-full object-cover grayscale-[0.2] transition-all duration-500 hover:scale-110 hover:grayscale-0" />
                      </div>
                    ))}
                  </div>
                </Block>
              </Reveal>
            )}

            {c.related_blogs?.length > 0 && (
              <Reveal>
                <Block number="04" title="İlgili rehber yazıları">
                  <div className="mt-2 grid gap-3 sm:grid-cols-2">
                    {c.related_blogs.map((b) => (
                      <Link key={b.slug} to={`/blog/${b.slug}`} data-testid={`application-blog-${b.slug}`} className="group flex items-center gap-4 overflow-hidden border border-white/10 bg-neutral-900 p-3 transition-colors hover:border-gold/40">
                        <img src={`${b.cover_image}?w=320`} alt={b.title} loading="lazy" decoding="async" className="h-20 w-24 shrink-0 object-cover grayscale-[0.2] transition group-hover:grayscale-0" />
                        <span className="min-w-0">
                          <span className="inline-flex items-center gap-1.5 font-oswald text-[10px] uppercase tracking-[0.15em] text-gold"><BookOpen className="h-3 w-3" /> Rehber</span>
                          <span className="mt-0.5 block font-medium leading-snug text-white transition-colors group-hover:text-gold line-clamp-2">{b.title}</span>
                        </span>
                      </Link>
                    ))}
                  </div>
                </Block>
              </Reveal>
            )}

            {c.related_applications?.length > 0 && (
              <Reveal>
                <Block number="05" title={`${c.district} bölgesindeki diğer uygulamalar`}>
                  <div className="mt-2 grid gap-3 sm:grid-cols-2">
                    {c.related_applications.map((a, i) => <ApplicationCard key={a.slug} app={a} index={i} />)}
                  </div>
                </Block>
              </Reveal>
            )}
          </div>

          {/* Sidebar */}
          <aside className="space-y-6 lg:sticky lg:top-28 lg:h-fit">
            <div className="border border-white/10 bg-gradient-to-b from-neutral-900 to-[#070707] p-6">
              <h3 className="font-display text-xl font-medium text-white">Uygulama bilgileri</h3>
              <ul className="mt-5 space-y-3">
                {facts.filter((f) => f.value).map((f) => {
                  const Icon = f.icon;
                  return (
                    <li key={f.label} className="flex items-start gap-3 border border-white/10 bg-white/[0.02] p-3.5">
                      <Icon className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
                      <span><span className="block text-xs text-neutral-500">{f.label}</span><span className="text-sm font-medium text-white">{f.value}</span></span>
                    </li>
                  );
                })}
              </ul>
              {c.services?.length > 0 && (
                <div className="mt-5 border-t border-white/10 pt-5">
                  <span className="flex items-center gap-2 font-oswald text-[11px] uppercase tracking-[0.15em] text-neutral-500"><Sparkles className="h-3.5 w-3.5 text-gold" /> Uygulanan hizmetler</span>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {c.services.map((s) => <span key={s} className="border border-white/10 bg-neutral-900 px-3 py-1.5 text-xs text-neutral-300">{s}</span>)}
                  </div>
                </div>
              )}
              <div className="mt-5 space-y-2.5 border-t border-white/10 pt-5">
                <Link to={`/randevu?hizmet=${encodeURIComponent(primaryService)}`} className="flex w-full items-center justify-center gap-2 bg-gold px-5 py-3.5 font-oswald text-sm font-bold uppercase tracking-[0.1em] text-black transition-colors hover:bg-gold-300">
                  <CalendarCheck className="h-[18px] w-[18px]" /> Randevu Al
                </Link>
                <a href={whatsappLink(settings, waMessage)} target="_blank" rel="noopener noreferrer" className="flex w-full items-center justify-center gap-2 border border-white/15 px-5 py-3.5 text-sm font-semibold text-white transition-colors hover:border-gold/40">
                  <MessageCircle className="h-[18px] w-[18px] text-gold" /> WhatsApp'tan Yaz
                </a>
                <a href={telLink(settings)} className="flex w-full items-center justify-center gap-2 border border-white/15 px-5 py-3.5 text-sm font-semibold text-white transition-colors hover:border-gold/40">
                  {settings?.phone}
                </a>
              </div>
            </div>

            {c.related_service_objs?.length > 0 && (
              <div className="border border-white/10 bg-white/[0.02] p-6">
                <h3 className="font-display text-lg font-medium text-white">İlgili hizmetler</h3>
                <ul className="mt-4 space-y-2.5">
                  {c.related_service_objs.map((r) => (
                    <li key={r.slug}>
                      <Link to={`/hizmetler/${r.slug}`} data-testid={`application-service-${r.slug}`} className="group flex items-center justify-between gap-2 border border-white/10 px-4 py-3 text-sm font-medium text-neutral-200 transition-colors hover:border-gold/40 hover:text-gold">
                        {r.name} <ArrowUpRight className="h-4 w-4 opacity-50 group-hover:opacity-100" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {(c.district_page || c.district_hub) && (
              <div className="border border-white/10 bg-white/[0.02] p-6">
                <h3 className="font-display text-lg font-medium text-white">{c.district} bölgesi</h3>
                <ul className="mt-4 space-y-2.5">
                  {c.district_page && (
                    <li>
                      <Link to={`/${c.district_page.slug}`} data-testid={`application-district-page-${c.district_page.slug}`} className="group flex items-center justify-between gap-2 border border-white/10 px-4 py-3 text-sm font-medium text-neutral-200 transition-colors hover:border-gold/40 hover:text-gold">
                        <span className="inline-flex items-center gap-2"><MapPin className="h-4 w-4 text-gold/70" /> {c.district_page.h1}</span>
                        <ArrowUpRight className="h-4 w-4 opacity-50 group-hover:opacity-100" />
                      </Link>
                    </li>
                  )}
                  {c.district_hub && (
                    <li>
                      <Link to={`/${c.district_hub.slug}`} data-testid={`application-district-hub-${c.district_hub.slug}`} className="group flex items-center justify-between gap-2 border border-white/10 px-4 py-3 text-sm font-medium text-neutral-200 transition-colors hover:border-gold/40 hover:text-gold">
                        <span className="inline-flex items-center gap-2"><MapPin className="h-4 w-4 text-gold/70" /> {c.district_hub.h1}</span>
                        <ArrowUpRight className="h-4 w-4 opacity-50 group-hover:opacity-100" />
                      </Link>
                    </li>
                  )}
                </ul>
              </div>
            )}
          </aside>
        </div>
      </section>
      <FinalCta />
    </>
  );
}
